import { useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@perceptron/ui/components/dialog";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import ClearButton from "@demo/components/PromptControls/ClearButton";
import RunButton from "@demo/components/PromptControls/RunButton";
import Settings from "@demo/components/Settings";
import { AgentState, useModelStore } from "@demo/store/model";

import { useFooterLabels } from "./useFooterLabels";

// Compose's clear control: the shared ClearButton on desktop, and a mobile
// confirm dialog (Compose-specific) so a tap doesn't wipe the prompt by accident.
const ClearPromptButton = ({
  hasAnyContent,
  onClearDocument,
}: {
  hasAnyContent: boolean;
  onClearDocument: () => void;
}) => {
  const [isClearPromptDialogOpen, setIsClearPromptDialogOpen] = useState(false);

  return (
    <>
      <div className="hidden sm:block">
        <ClearButton
          disabled={!hasAnyContent}
          tooltip={strings.promptView.tooltips.clearPrompt}
          onClear={onClearDocument}
        />
      </div>
      <Dialog open={isClearPromptDialogOpen} onOpenChange={setIsClearPromptDialogOpen}>
        <DialogTrigger asChild className="block sm:hidden">
          <Button
            className="m-0 h-auto w-fit p-0 hover:bg-transparent"
            disabled={!hasAnyContent}
            size="sm"
            variant="ghost"
          >
            <Icon
              className={cn("size-5 transition-opacity", {
                "opacity-90": hasAnyContent,
                "opacity-50": !hasAnyContent,
              })}
              name="clear"
            />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-sm rounded-lg border-neutral-800 bg-neutral-900 p-6 shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-left text-lg font-semibold text-white">
              {strings.promptView.clearPromptDialog.title}
            </DialogTitle>
            <DialogDescription className="text-left text-sm text-neutral-400">
              {strings.promptView.clearPromptDialog.description}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 flex justify-end gap-3">
            <Button
              className="border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700 hover:text-white"
              variant="outline"
              onClick={() => setIsClearPromptDialogOpen(false)}
            >
              {strings.promptView.clearPromptDialog.cancelButton}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onClearDocument();
                setIsClearPromptDialogOpen(false);
              }}
            >
              {strings.promptView.clearPromptDialog.confirmButton}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ComposeFooter = ({
  onSubmit,
  isReadyToRun,
  hasAnyContent,
  onClearDocument,
}: {
  onSubmit: () => void;
  isReadyToRun: boolean;
  hasAnyContent: boolean;
  onClearDocument: () => void;
}) => {
  const agentState = useModelStore((state) => state.agentState);
  const stopStreaming = useModelStore((state) => state.stopStreaming);
  const [footerRef, showLabels] = useFooterLabels();

  const isStreaming = agentState === AgentState.STREAMING || agentState === AgentState.EVALUATING;

  return (
    <div
      className={cn(
        "absolute bottom-0 right-0 flex w-full items-center p-4 transition-[transform,opacity] duration-300 sm:relative sm:p-0",
        {
          "pointer-events-none opacity-0 duration-200 sm:pointer-events-auto sm:opacity-100": !hasAnyContent,
          "opacity-100 delay-200 sm:opacity-100": hasAnyContent,
        },
      )}
      id="footer-container"
      ref={footerRef}
    >
      <div className="flex w-full items-center justify-between gap-1">
        <div className="flex items-center gap-1">
          <Settings showLabels={showLabels} />
          <div
            aria-hidden="true"
            className="footer-divider my-1 w-0.5 self-stretch rounded-full bg-foreground-secondary opacity-30"
          />
          <ClearPromptButton hasAnyContent={hasAnyContent} onClearDocument={onClearDocument} />
        </div>
        <div className="flex items-center gap-1">
          <RunButton
            className="rounded-full sm:w-auto sm:rounded-md"
            disabled={!isReadyToRun && !isStreaming}
            isStreaming={isStreaming}
            runLabel={strings.promptView.buttons.run}
            stopLabel={strings.promptView.buttons.stop}
            onRun={onSubmit}
            onStop={stopStreaming}
          />
        </div>
      </div>
    </div>
  );
};

export default ComposeFooter;
